import React from "react";
import { ImageUploadField } from "../../shared/ImageUploadField";
import { MultiImageUploadField } from "../../shared/MultiImageUploadField";
import { DetailContentModePicker } from "./DetailContentModePicker";
import { DetailSectionsEditor } from "./DetailSectionsEditor";
import { RichContentEditorSection } from "./RichContentEditorSection";
import { blogInputClass } from "./BlogEditorFormFields";

export const defaultProductForm = {
  id: 0,
  customerId: "",
  code: "",
  barcode: "",
  name: "",
  subtitle: "",
  shortDescription: "",
  description: "",
  content: "",
  detailContentMode: "html",
  sectionsJson: "",
  categoryId: "",
  brandId: "",
  unitName: "",
  price: 0,
  discountPrice: "",
  minQuantity: 1,
  image: "",
  order: 0,
  images: []
};

const fieldLabelClass = "mb-1.5 block text-xs font-medium text-slate-600";

export function ProductEditorFormFields({
  form,
  setForm,
  isAdmin,
  customers,
  categories,
  brands,
  htmlMode,
  setHtmlMode,
  showGallery = true
}) {
  return (
    <div className="space-y-4">
      {isAdmin ? (
        <div>
          <label className={fieldLabelClass} htmlFor="product-customer">
            Customer <span className="text-red-500">*</span>
          </label>
          <select
            id="product-customer"
            required
            className={blogInputClass}
            value={form.customerId}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                customerId: e.target.value,
                categoryId: "",
                brandId: ""
              }))
            }
          >
            <option value="">— Select —</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} {c.code ? `(${c.code})` : ""}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className={fieldLabelClass}>Code</label>
          <input
            className={blogInputClass}
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
          />
        </div>
        <div>
          <label className={fieldLabelClass}>Barcode</label>
          <input
            className={blogInputClass}
            value={form.barcode}
            onChange={(e) => setForm((f) => ({ ...f, barcode: e.target.value }))}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className={fieldLabelClass}>
            Name <span className="text-red-500">*</span>
          </label>
          <input
            className={blogInputClass}
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
          />
        </div>
        <div>
          <label className={fieldLabelClass}>Subtitle</label>
          <input
            className={blogInputClass}
            value={form.subtitle}
            onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className={fieldLabelClass}>Short description</label>
          <textarea
            className={blogInputClass}
            rows={3}
            value={form.shortDescription}
            onChange={(e) => setForm((f) => ({ ...f, shortDescription: e.target.value }))}
          />
        </div>
        <div>
          <label className={fieldLabelClass}>Catalog description</label>
          <textarea
            className={blogInputClass}
            rows={3}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className={fieldLabelClass}>
            Category <span className="text-red-500">*</span>
          </label>
          <select
            className={blogInputClass}
            value={form.categoryId}
            onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))}
            required
          >
            <option value="">Select category</option>
            {categories.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.name || c.code || `Category ${c.id}`}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={fieldLabelClass}>Brand</label>
          <select
            className={blogInputClass}
            value={form.brandId}
            onChange={(e) => setForm((f) => ({ ...f, brandId: e.target.value }))}
          >
            <option value="">No brand</option>
            {brands.map((b) => (
              <option key={b.id} value={String(b.id)}>
                {b.name || `Brand ${b.id}`}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label className={fieldLabelClass}>Unit</label>
          <input
            className={blogInputClass}
            value={form.unitName}
            onChange={(e) => setForm((f) => ({ ...f, unitName: e.target.value }))}
          />
        </div>
        <div>
          <label className={fieldLabelClass}>Price</label>
          <input
            className={blogInputClass}
            type="number"
            step="0.01"
            value={form.price}
            onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
          />
        </div>
        <div>
          <label className={fieldLabelClass}>Discount price</label>
          <input
            className={blogInputClass}
            type="number"
            step="0.01"
            value={form.discountPrice}
            onChange={(e) => setForm((f) => ({ ...f, discountPrice: e.target.value }))}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className={fieldLabelClass}>Min quantity</label>
          <input
            className={blogInputClass}
            type="number"
            value={form.minQuantity}
            onChange={(e) => setForm((f) => ({ ...f, minQuantity: e.target.value }))}
          />
        </div>
        <div>
          <label className={fieldLabelClass}>Order</label>
          <input
            className={blogInputClass}
            type="number"
            value={form.order}
            onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))}
          />
        </div>
      </div>

      <ImageUploadField
        label="Primary image"
        value={form.image}
        onChange={(v) => setForm((f) => ({ ...f, image: v }))}
        labelClassName={fieldLabelClass}
      />

      {showGallery ? (
        <MultiImageUploadField
          label="Product gallery images"
          value={form.images}
          onChange={(next) => setForm((f) => ({ ...f, images: next }))}
          labelClassName={fieldLabelClass}
        />
      ) : null}

      <DetailContentModePicker
        value={form.detailContentMode}
        onChange={(detailContentMode) => setForm((f) => ({ ...f, detailContentMode }))}
      />

      {form.detailContentMode === "json" ? (
        <DetailSectionsEditor form={form} setForm={setForm} />
      ) : (
        <RichContentEditorSection
          form={form}
          setForm={setForm}
          htmlMode={htmlMode}
          setHtmlMode={setHtmlMode}
          label="Landing page content (HTML)"
          fieldId="product-content"
          description="Blog-style freeform landing page — headings, text, images, tables, columns, and buttons."
        />
      )}
    </div>
  );
}
